import { memo } from "react"
import { Link as ReactRouterLink } from "react-router-dom"
import { Trans, useTranslation } from "react-i18next"
import { DateTime } from "luxon"
import { Invoice, InvoiceKind, InvoiceSubkind } from "@oysterhr/schema"
import { Button, Heading, Link, Text } from "@oysterhr/ui"
import { SimpleMoney } from "@oysterhr/common"
import { Card, Drawer, Row } from "../../Viewer"
import { useAccountAspect } from "@oysterhr/identity/AccountAspectContext"
import { InvoiceSubkindLabelsMap } from "../../../constants/invoices"
import { ClientInvoiceStateBadge } from "../../ClientInvoiceStateBadge"
import { InvoicePaymentStatePill, InvoiceReadOnlyPayoutStatePill } from "../../"
import { useInvoicePaymentHistory } from "../hooks/useInvoicePaymentHistory"
import { PaymentHistoryItemType } from "../constants"
import { companyCreditNotesPath, staffCreditNotesPath } from "../../../utils/routeHelpers"
import { getClientInvoiceAmount } from "../../../utils/getClientInvoiceAmount"
import { PayoutTimeline } from "./PayoutTimeline/PayoutTimeline"
import { InvoiceDueDateValue } from "../../InvoiceDueDateValue"

interface Props {
  invoice: Invoice
}

export const InvoiceDrawer = memo(({ invoice }: Props) => {
  const { t } = useTranslation("Invoices")
  const { isStaff, isContractor } = useAccountAspect()

  const kindLabel = InvoiceSubkindLabelsMap[invoice.subkind]
  const isContractorInvoice = invoice.kind === InvoiceKind.Contractor
  const paymentHistory = useInvoicePaymentHistory({ invoice })

  return (
    <Drawer>
      <div className="flex flex-col gap-4">
        {invoice.file?.url && (
          <Button as={ReactRouterLink} to={invoice.file.url} isFullWidth>
            {t("drawer.downloadDocument")}
          </Button>
        )}
        {invoice.supportingFile?.url && (
          <Button
            as={ReactRouterLink}
            to={invoice.supportingFile.url}
            isFullWidth
            variation="secondary"
          >
            {t("drawer.downloadBreakdown")}
          </Button>
        )}
      </div>
      <div className="mt-8 flex flex-col gap-4">
        <Card>
          <div className="flex flex-col gap-2">
            <Heading size="xl">{invoice.referenceCode}</Heading>
            <div className="flex flex-wrap items-center justify-between">
              <Text size="medium" className="font-medium">
                {t(kindLabel.translationKey)}
              </Text>
              <div className="whitespace-nowrap">
                <ClientInvoiceStateBadge invoice={invoice} />
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {isStaff && <Row label={t("drawer.customer")}>{invoice.company.name}</Row>}
            <Row label={t("drawer.payrollMonth")}>{invoice.paymentPeriod}</Row>
            <Row label={t("drawer.issueDate")}>
              {invoice.invoiceDate
                ? DateTime.fromISO(invoice.invoiceDate).toLocaleString(DateTime.DATE_MED)
                : "-"}
            </Row>
            <Row label={t("drawer.dueDate")}>
              <InvoiceDueDateValue invoice={invoice} />
            </Row>
            {isStaff && isContractorInvoice && (
              <Row label={t("drawer.payoutOrderId")} tooltipText={t("drawer.payoutOrderIdTooltip")}>
                {invoice.payoutPaymentRequest.idempotencyKey}
              </Row>
            )}
            <Row
              label={
                isContractorInvoice
                  ? t("drawer.contractedInvoiceAmount")
                  : t("drawer.invoiceAmount")
              }
            >
              {SimpleMoney.fromNullable(getClientInvoiceAmount(invoice))?.format()}
            </Row>
            {invoice.subkind === InvoiceSubkind.OysterSettlement && invoice.totalAmount && (
              <Row label={t("drawer.totalMonthCost")}>
                {SimpleMoney.fromNullable(invoice.totalAmount)?.format()}
              </Row>
            )}
          </div>
        </Card>
        {!isContractor && (
          <Card>
            <div className="flex flex-wrap items-center justify-between">
              <Text className="font-medium">{t("drawer.paymentStatus")}</Text>
              <div className="whitespace-nowrap">
                <InvoicePaymentStatePill chargeState={invoice.chargePaymentRequest.ordersState} />
              </div>
            </div>

            {isContractorInvoice ? (
              <Row label={t("drawer.invoiceAmount")}>
                <Heading size="2xl">
                  {SimpleMoney.fromNullable(invoice.chargePaymentRequest.targetAmount)?.format()}
                </Heading>
              </Row>
            ) : (
              <Row label={t("drawer.amountDue")}>
                <Heading size="2xl">
                  {SimpleMoney.fromNullable(invoice.amountDue)?.format()}
                </Heading>
              </Row>
            )}
            {paymentHistory.length > 0 && (
              <div className="flex flex-col gap-2">
                {paymentHistory.map(({ date, amount, reference, type }, index) => (
                  <Row label={date.toLocaleString(DateTime.DATE_MED)} key={index}>
                    <Text size="medium" className="text-gray-400">
                      {type === PaymentHistoryItemType.CreditNote ? (
                        <Trans
                          i18nKey="drawer.appliedCreditNote"
                          t={t}
                          values={{
                            amount: SimpleMoney.fromNullable(amount)?.format(),
                            referenceCode: reference,
                          }}
                          components={{
                            a: (
                              <Link
                                className="inline font-normal"
                                target="_blank"
                                to={
                                  isStaff
                                    ? staffCreditNotesPath(`query=${reference}`)
                                    : companyCreditNotesPath(
                                        invoice.company.id,
                                        `query=${reference}`,
                                      )
                                }
                              />
                            ),
                          }}
                        />
                      ) : (
                        t("drawer.paidVia", {
                          amount: SimpleMoney.fromNullable(amount)?.format(),
                          provider: reference,
                        })
                      )}
                    </Text>
                  </Row>
                ))}
              </div>
            )}
          </Card>
        )}
        {isContractorInvoice && (
          <Card>
            <div className="flex flex-wrap items-center justify-between">
              <Text className="font-medium">{t("drawer.payoutStatus")}</Text>
              <div className="whitespace-nowrap">
                <InvoiceReadOnlyPayoutStatePill state={invoice.payoutPaymentRequest.viewState} />
              </div>
            </div>

            <PayoutTimeline timeline={invoice.payoutPaymentRequest.timeline} />
            <Row label={t("drawer.payoutAmount")}>
              <Heading size="2xl">
                {SimpleMoney.fromNullable(invoice.payoutPaymentRequest.targetAmount)?.format()}
              </Heading>
            </Row>
          </Card>
        )}
      </div>
    </Drawer>
  )
})
